import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const reviews = [
  // Most recent Google Reviews
  { name: "Martin Schouten", location: "Google Review", rating: 5, text: "Via onze energieadviseur zijn we in contact gekomen met Helios Bright voor een thuisbatterij bij onze boerderij. Alles verliep soepel, afspraken werden nagekomen en de installatie is netjes uitgevoerd. We zijn erg tevreden over het gehele traject." },
  { name: "Harm Kamminga", location: "Google Review", rating: 5, text: "Zeer tevreden over de service van Helios Bright. Duidelijke communicatie, snelle opvolging en een nette installatie. Alles is volgens afspraak verlopen en er werd goed meegedacht over de beste oplossing voor onze situatie." },
  { name: "Yousra Thus", location: "Google Review", rating: 5, text: "Positieve ervaring met Helios Bright. Er waren een paar punten waar ik extra uitleg over nodig had, maar daar werd snel op gereageerd. Uiteindelijk is alles naar tevredenheid verlopen." },
  { name: "Chiel Caspers", location: "Google Review", rating: 5, text: "Onlangs gekozen voor Helios Bright voor de installatie van zonnepanelen. Vanaf het eerste contact een goed gevoel gehad. Casper was makkelijk bereikbaar, dacht mee en de installatie is professioneel uitgevoerd." },
  { name: "Patrick V.d. Molen", location: "Google Review", rating: 5, text: "Onlangs een BYD thuisbatterij gekocht via Helios Bright. Prettig contact, duidelijke uitleg en de installatie verliep zonder problemen. Tot nu toe erg tevreden." },
  // Earlier reviews
  { name: "Mark de Vries", location: "Groningen", rating: 5, text: "Helios Bright heeft onze Tesla Powerwall vlekkeloos geïnstalleerd. Adviseur was deskundig en de monteurs supernetjes. We zien onze besparing direct terug." },
  { name: "Sanne Bakker", location: "Haren", rating: 5, text: "Eindelijk een installateur die écht meedenkt. Combinatie van extra panelen + BYD batterij. Dashboard via app werkt geweldig." },
  { name: "Joost Visser", location: "Zuidhorn", rating: 5, text: "Van offerte tot oplevering binnen 4 weken. Premium kwaliteit, geen verrassingen. Aanrader voor iedereen met zonnepanelen." },
  { name: "Linda Janssen", location: "Assen", rating: 5, text: "Top service! Goede uitleg over werking en saldering. We zijn nu bijna 90% zelfvoorzienend met onze Huawei Luna." },
  { name: "Peter Mulder", location: "Leek", rating: 5, text: "Professionele installateurs en strakke afwerking. De besparingscalculator klopte exact met de realiteit." },
];

const ReviewsCarousel = () => {
  const { t } = useLang();
  const [i, setI] = useState(0);
  const next = () => setI((p) => (p + 1) % reviews.length);
  const prev = () => setI((p) => (p - 1 + reviews.length) % reviews.length);

  return (
    <section className="section-pad bg-white" data-testid="reviews-carousel">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">{t.reviews.eyebrow}</p>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">{t.reviews.title}</h2>
        </div>

        <div className="relative max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="bg-gradient-to-br from-white to-brand-soft/30 border border-brand/10 rounded-[2rem] p-8 md:p-12 shadow-[0_30px_80px_rgba(15,23,42,0.08)] relative"
              data-testid="review-active"
            >
              <Quote className="absolute top-7 right-7 text-brand/20" size={56} />
              <div className="flex gap-1 mb-5">
                {[...Array(reviews[i].rating)].map((_, x) => (
                  <Star key={x} className="fill-yellow-400 text-yellow-400" size={20} />
                ))}
              </div>
              <p className="font-heading text-2xl md:text-3xl font-medium text-navy-900 leading-snug text-balance">"{reviews[i].text}"</p>
              <div className="mt-7 flex items-center gap-3">
                <div className="w-12 h-12 rounded-full gradient-pink flex items-center justify-center font-heading font-black text-white">
                  {reviews[i].name.charAt(0)}
                </div>
                <div>
                  <div className="font-semibold text-navy-900">{reviews[i].name}</div>
                  <div className="text-sm text-slate-500">{reviews[i].location}</div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center justify-between mt-8">
            <div className="flex gap-2">
              {reviews.map((_, x) => (
                <button
                  key={x}
                  onClick={() => setI(x)}
                  className={`h-2 rounded-full transition-all ${x === i ? "w-8 bg-brand" : "w-2 bg-slate-300"}`}
                  data-testid={`review-dot-${x}`}
                  aria-label={`Review ${x + 1}`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={prev} className="w-12 h-12 rounded-full border border-slate-200 hover:border-brand hover:text-brand transition-colors flex items-center justify-center" data-testid="review-prev"><ChevronLeft size={20} /></button>
              <button onClick={next} className="w-12 h-12 rounded-full gradient-pink text-white flex items-center justify-center hover:shadow-[0_8px_24px_rgba(255,47,125,0.4)] transition-shadow" data-testid="review-next"><ChevronRight size={20} /></button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReviewsCarousel;
